
// --[[
// 	https://github.com/gre/bezier-easing
// 	BezierEasing - use bezier curve for transition easing function
// 	by Gaëtan Renaudeau 2014 - 2015 – MIT License
// --]]
// Utility function to create a bezier easing function
export const bezierEasing = (x1, y1, x2, y2) => {
    const NEWTON_ITERATIONS = 4;
    const NEWTON_MIN_SLOPE = 0.001;
    const SUBDIVISION_PRECISION = 0.0000001;
    const SUBDIVISION_MAX_ITERATIONS = 10;
    const KSplineTableSize = 11;
    const KSampleStepSize = 1.0 / (KSplineTableSize - 1.0);

    const sampleValues = new Float32Array(KSplineTableSize);
    for (let i = 0; i < KSplineTableSize; ++i) {
        sampleValues[i] = calcBezier(i * KSampleStepSize, x1, x2);
    }

    function calcBezier(t, a1, a2) {
        return ((3 * a1 - 3 * a2 + 1) * t + (-6 * a1 + 3 * a2)) * t * t + (3 * a1) * t;
    }

    function getSlope(t, a1, a2) {
        return 3 * (1 - 3 * a2 + 3 * a1) * t * t + 2 * (3 * a2 - 6 * a1) * t + 3 * a1;
    }

    function binarySubdivide(aX, aA, aB, mX1, mX2) {
        let currentX, currentT, i = 0;
        do {
            currentT = aA + (aB - aA) / 2.0;
            currentX = calcBezier(currentT, mX1, mX2) - aX;
            if (currentX > 0) {
                aB = currentT;
            } else {
                aA = currentT;
            }
        } while (Math.abs(currentX) > SUBDIVISION_PRECISION && ++i < SUBDIVISION_MAX_ITERATIONS);
        return currentT;
    }

    function newtonRaphsonIterate(aX, guessT, mX1, mX2) {
        for (let i = 0; i < NEWTON_ITERATIONS; ++i) {
            const currentSlope = getSlope(guessT, mX1, mX2);
            if (currentSlope === 0) return guessT;
            const currentX = calcBezier(guessT, mX1, mX2) - aX;
            guessT -= currentX / currentSlope;
        }
        return guessT;
    }

    function getTForX(aX) {
        let intervalStart = 0.0;
        let currentSample = 1;
        const lastSample = KSplineTableSize - 1;
        for (; currentSample !== lastSample && sampleValues[currentSample] <= aX; ++currentSample) {
            intervalStart += KSampleStepSize;
        }
        --currentSample;
        const dist = (aX - sampleValues[currentSample]) / (sampleValues[currentSample + 1] - sampleValues[currentSample]);
        const guessForT = intervalStart + dist * KSampleStepSize;
        const initialSlope = getSlope(guessForT, x1, x2);
        if (initialSlope >= NEWTON_MIN_SLOPE) {
            return newtonRaphsonIterate(aX, guessForT, x1, x2);
        } else if (initialSlope === 0) {
            return guessForT;
        } else {
            return binarySubdivide(aX, intervalStart, intervalStart + KSampleStepSize, x1, x2);
        }
    }

    return function (t) {
        if (x1 === y1 && x2 === y2) return t; // linear
        if (t === 0 || t === 1) return t; // precise endpoints
        return calcBezier(getTForX(t), y1, y2);
    };
};
// Utility function to interpolate values
export const interpolate = (start, end, progress) => {
    if (typeof start === 'number' && typeof end === 'number') {
        return start + (end - start) * progress;
    }
    return end;
};

export const applyStyles = (element, styles) => {
    Object.keys(styles).forEach((key) => {
        if (key === 'transform') {
            element.style[key] = `${styles[key]} translateZ(0px)`;
        } else {
            element.style[key] = styles[key];
        }
    });
};